<template>
    <div v-if="showPopup" ref="segmentationFixConfirmPopup" data-compType="segmentationFixConfirmPopup" class="segmentation-fix-confirm-popup"
        :style="{ '--left-position': currentLeft + 'px', '--top-position': currentTop + 'px' }">
        <div class="header">
            <section class="header__content">
                <span class="header__drag-handle" :class="{ 'is-dragging': drag.isActive }" title="Mover"
                    @mousedown.prevent="startDrag">
                    <DragHandleIconSvg />
                </span>
                <h5 class="title">
                    Confirmar correção do dente
                </h5>
            </section>
            <span class="header__close" @click="closePopup">
                <CloseSvgIcon />
            </span>
        </div>
        <div class="content">

            <section class="seletor-dente">
                <selectUI type="single" label="Dente" labelPlacement="inside" placeholder="Selecione o dente"
                    :minWidth="120" :panelMaxHeight="120" :options="options.dentes.lista"
                    :defaultSelected="current.dente || null"
                    @change="onChangeSelectedDente($event)">
                    <template #optionLabel="{ option }">
                        <reportLabelAnnotationItem
                            :disabled="true"
                            :annotation="{ id: option.value, reportText: option.label, color: option.color, localizacao: {} }" />
                    </template>
                </selectUI>
            </section>

        </div>
        <div class="footer">
            <secondaryButton class="button--cancel" variant="outline" @click="onCancel">Cancelar</secondaryButton>
            <secondaryButton class="button--save" variant="solid" :disabled="!current.dente" @click="onSave">Confirmar</secondaryButton>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { evtBus } from '@/main';
import { gConsts } from '@/globals/gConsts';
import { nodes } from '@/globals/nodes';
import { utils } from '@/globals/utils';
import { toothUtils } from '@/features/tooth/lib/toothUtils';

import CloseSvgIcon from '@/assets/icons/close.svg';
import DragHandleIconSvg from '@/assets/icons/drag-handle.svg';
import SecondaryButton from '@/components/ui/button/secondaryButton.vue';

import selectUI from '@/components/ui/select/select.vue';
import reportLabelAnnotationItem from '@/features/report/components/text/reportLabelAnnotationItem.vue';


export default {
    name: 'segmentationFixConfirmPopup',
    components: {
        CloseSvgIcon,
        DragHandleIconSvg,
        SecondaryButton,
        selectUI,
        reportLabelAnnotationItem,
    },
    props: {
        show: {
            type: Boolean,
            default: false,
        },
        leftPosition: {
            type: Number,
            default: 0,
        },
        topPosition: {
            type: Number,
            default: 0,
        },
        result: {
            type: Object,
        },
        selection: {
            type: Object,
        },
    },

    data() {
        return {
            showPopup: this.show,
            drag: {
                offsetX: 0,
                offsetY: 0,
                startX: 0,
                startY: 0,
                startOffsetX: 0,
                startOffsetY: 0,
                isActive: false,
            },
            options: {
                dentes: {
                    lista: []
                }
            },
            current: {
                dente: this.result?.dente || null,
            }
        }
    },

    computed: {
        ...mapGetters([
            'GET_teethSegTools_sorted',
            'GET_dentesNodes',
            'GET_toothRegionRect',
        ]),
        currentLeft() {
            return this.leftPosition + this.drag.offsetX;
        },
        currentTop() {
            return this.topPosition + this.drag.offsetY;
        },
    },

    watch: {
        show: {
            handler(newVal) {
                this.showPopup = newVal;
                if (newVal) {
                    this.drag.offsetX = 0;
                    this.drag.offsetY = 0;
                    this.$nextTick(() => {
                        document.addEventListener('mousedown', this.onClickOutside);
                    });
                } else {
                    document.removeEventListener('mousedown', this.onClickOutside);
                }
            },
            immediate: true
        }
    },

    methods: {

        startDrag(event) {
            this.drag.isActive = true;
            this.drag.startX = event.clientX;
            this.drag.startY = event.clientY;
            this.drag.startOffsetX = this.drag.offsetX;
            this.drag.startOffsetY = this.drag.offsetY;

            document.addEventListener('mousemove', this.onDragMove);
            document.addEventListener('mouseup', this.stopDrag);
        },

        onDragMove(event) {
            if (!this.drag.isActive) return;
            this.drag.offsetX = this.drag.startOffsetX + (event.clientX - this.drag.startX);
            this.drag.offsetY = this.drag.startOffsetY + (event.clientY - this.drag.startY);
        },

        stopDrag() {
            this.drag.isActive = false;
            document.removeEventListener('mousemove', this.onDragMove);
            document.removeEventListener('mouseup', this.stopDrag);
        },

        onClickOutside(event) {
            const popup = this.$refs.segmentationFixConfirmPopup;
            if (popup && !popup.contains(event.target)) {
                this.closePopup();
            }
        },

        closePopup() {
            this.showPopup = false;
            this.$emit('onClose');
        },

        onCancel() {
            this.$emit('onClose');
        },

        onSave() {
            this.$emit('onSave', {
                ...this.result,
                dente: this.current.dente,
            });
        },

        onChangeSelectedDente(event) {
            this.current.dente = event;
        },

        mapNodeToSelectOption(node) {
            const color = gConsts.ui.prettyColors[node?.layout?.color?.toLowerCase()] || node?.layout?.color;
            return {
                value: node.id,
                label: node.layout.caption,
                color: color,
            };
        },

        populateDentesOptions() {
            if (this.options.dentes.lista.length > 0)
                return;

            let denteToolsProximos = []
            // Uso o dobro da distancia normal para dar possibilidade de outros dentes.
            const maxDistance = this.GET_toothRegionRect.width * 2;
            const position = this.selection.mousePos;
            denteToolsProximos = utils.findCloseDenteToolsFromPosition(this.GET_teethSegTools_sorted, position, maxDistance);
            if (denteToolsProximos.length > 0) {
                const closestDenteTool = denteToolsProximos[0];
                const toothDetails = toothUtils.getToothDetailsByLabelId(closestDenteTool.label.id);
                const closestDenteOrderIndex = toothUtils.getToothOrderIndex(toothDetails.numero, 'complete');
                // Ordeno os dentes mais proximos da posicao da arcada do dente mais proximo
                // O dente mais proximo ficara no topo da lista
                const closeDenteNodes = this.GET_dentesNodes
                    .sort((a,b) => {
                        const aOrderIndex = toothUtils.getToothOrderIndex(+a.layout.caption, 'complete');
                        const bOrderIndex = toothUtils.getToothOrderIndex(+b.layout.caption, 'complete');
                        return Math.abs(aOrderIndex - closestDenteOrderIndex) - Math.abs(bOrderIndex - closestDenteOrderIndex);
                    }).slice(0, 5);
                if (this.result.dente) {
                    const resultDenteNode = this.GET_dentesNodes.find(dente => dente.id === this.result.dente);
                    if (resultDenteNode) {
                        closeDenteNodes.splice(closeDenteNodes.indexOf(resultDenteNode), 1);
                        closeDenteNodes.unshift(resultDenteNode);
                    }
                }
                this.options.dentes.lista = [
                    {
                        value: 'proximos',
                        label: 'Próximos',
                        children: closeDenteNodes.map(this.mapNodeToSelectOption),
                    },
                    {
                        value: 'outros',
                        label: 'Outros Dentes',
                        children: this.GET_dentesNodes
                            .filter(dente => !closeDenteNodes.some(denteNode => denteNode.id === dente.id))
                            .map(this.mapNodeToSelectOption),
                    }
                ];
            } else {
                this.options.dentes.lista = this.GET_dentesNodes
                    .map(this.mapNodeToSelectOption);
            }

        },

    },

    mounted() {

        this.populateDentesOptions();
    },

    beforeDestroy() {
        document.removeEventListener('mousemove', this.onDragMove);
        document.removeEventListener('mouseup', this.stopDrag);
        document.removeEventListener('mousedown', this.onClickOutside);
    }
}
</script>

<style scoped>
.segmentation-fix-confirm-popup {
    position: fixed;
    top: var(--top-position, 0px);
    left: var(--left-position, 0px);
    z-index: 1000;
    background: white;
    border: 1px solid var(--border-primary);
    border-radius: var(--card-border-radius);
    box-shadow: var(--card-box-shadow);
    /* width: 550px; */
    overflow: hidden;
    margin-top: var(--spacing-xs);
    animation: fadeIn 0.3s ease-in-out;
}

.segmentation-fix-confirm-popup>.header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-md);
    padding: var(--spacing-md);
    text-align: left;
    background-color: var(--background-gray);
    border-bottom: 1px solid var(--border-primary);
}

.segmentation-fix-confirm-popup .header__content {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--spacing-sm);
}

.segmentation-fix-confirm-popup .header__drag-handle {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-dark-gray);
    cursor: grab;
    touch-action: none;
    user-select: none;
    transition: color 0.2s ease, scale 0.2s ease;
}

.segmentation-fix-confirm-popup .header__drag-handle:hover {
    color: var(--color-primary);
    scale: 1.15;
}

.segmentation-fix-confirm-popup .header__drag-handle:active,
.segmentation-fix-confirm-popup .header__drag-handle.is-dragging {
    cursor: grabbing;
    color: var(--color-primary);
}

.segmentation-fix-confirm-popup .header__drag-handle>svg {
    width: 16px;
    height: 16px;
    pointer-events: none;
}

.segmentation-fix-confirm-popup .header__content .title {
    margin: 0;
    font-size: var(--text-md);
    line-height: 1;
    font-weight: 700;
    color: var(--color-primary);
}

.segmentation-fix-confirm-popup .header__close {
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: flex-start;
    padding: var(--spacing-xs);
    color: var(--color-dark-gray);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: all 0.2s ease;
}

.segmentation-fix-confirm-popup .header__close:hover {
    background-color: var(--background-light-gray);
}

.segmentation-fix-confirm-popup .header__close:active {
    scale: 0.98;
}

.segmentation-fix-confirm-popup .header__close>svg {
    width: 15px;
    height: 15px;
}

.segmentation-fix-confirm-popup>.content {
    width: 100%;
    max-width: 550px;
    min-height: 150px;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
    padding: var(--spacing-md);
    box-sizing: border-box;
}

.segmentation-fix-confirm-popup .preview-relatorio {
    flex: 1;
    width: 100%;
}

.segmentation-fix-confirm-popup>.footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--spacing-sm);
    padding: var(--spacing-md);
    background-color: var(--background-gray);
    border-top: 1px solid var(--border-primary);
}

.segmentation-fix-confirm-popup > .footer .button--cancel,
.segmentation-fix-confirm-popup > .footer .button--save {
    min-width: 100px;
    text-align: center;
    justify-content: center;
}

.segmentation-fix-confirm-popup > .footer .button--save {
    min-width: 100px;
}

</style>
